import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { usePostHog } from "posthog-js/react";
import {
  ApolloProvider,
  ApolloClient,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from '@apollo/client/link/context';
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import relayTheme from "./css/authTheme";
import { redirectToAuth } from "./utils/authUtils";
import {
  getStoredTokens,
  storeTokens,
  parseTokensFromHash,
  clearHashFromUrl,
  getIdToken,
} from "./utils/tokenUtils";
import { Menu } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import AppSidebar from "./components/AppSidebar";
import terms from "./assets/role_relay_terms.pdf";
import Home from "./components/Home";
import Settings from "./components/Settings";
import UserInvitation from "./components/UserInvitation";
import PublicRoles from "./components/PublicRoles";
import PublicCandidates from "./components/PublicCandidates";
import Candidates from "./components/Candidates";
import Roles from "./components/Roles";
import Messages from "./components/Messages";
import AddToProject from "./components/JobAdder/AddToProject";
import { JobAdderCallback } from "./components/JobAdder";
import { VincereCallback } from "./components/Vincere";
import { BullhornCallback } from "./components/Bullhorn";
import config from "./config";
import Dashboard from "./components/Dashboard";
import Footer from "./components/Footer";
import Privacy from "./components/Privacy";
import Dispute from "./components/Dispute";
import Join from "./components/Join";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { GET_CONNECTIONS, GET_PROFILE } from "./GraphQL/settings/queries";
import PublicMatches from "./components/PublicMatches";
import NewCompany from "./components/NewCompany";
import NewCompanyUser from "./components/NewCompanyUser";
import BullhornAction from "./components/Bullhorn/BullhornAction";
import Experiments from "./components/Experiments";

export const ProfileContext = React.createContext({
  connections: {} as any,
  userProfile: {} as any,
});

const Main = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const AppShell = styled.div<{ $withSidebar?: boolean; $sidebarCollapsed?: boolean }>`
  position: relative;
  min-height: 100vh;
  padding-top: 0;
  transition: padding-left 220ms ease;
  ${(props) =>
    props.$withSidebar &&
    `
    @media (min-width: 1024px) {
      padding-left: ${props.$sidebarCollapsed ? '64px' : '240px'};
    }
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ContentShell = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;

const MobileMenuButton = styled.button`
  display: none;

  @media (max-width: 1024px) {
    display: flex;
    position: fixed;
    top: 1rem;
    left: 1rem;
    z-index: 1001;
    width: 44px;
    height: 44px;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: #227b7e;
    color: white;
    border: none;
    box-shadow: 0 4px 12px rgba(34, 123, 126, 0.3);
    cursor: pointer;
    transition: all 150ms ease;

    &:hover {
      background: #0f4d4f;
      transform: scale(1.05);
    }

    &:active {
      transform: scale(0.98);
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
      &:hover {
        transform: none;
      }
      &:active {
        transform: none;
      }
    }
  }
`;

const DownMaintenance = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  font-family: "Barlow", sans-serif !important;
`;

// Apollo Client with token-based authentication
// Uses ID token from sessionStorage for Authorization header

const httpLink = new HttpLink({
  uri: config.apiUrl,
});

const authLink = setContext((_, { headers }) => {
  // Get the authentication token from sessionStorage
  const token = getIdToken();

  // Return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
});

const App = (): JSX.Element => {
  const posthog = usePostHog();
  const [connections, setConnections] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  const showMatchesFlag = useFeatureFlagEnabled(
    config?.postHog?.featureFlags?.showMatchesPage
  );
  const isMaintenanceFlag = useFeatureFlagEnabled(
    config?.postHog?.featureFlags?.isDownMaintenance
  );
  const isDashboardFlag = useFeatureFlagEnabled(
    config?.postHog?.featureFlags?.showDashboard
  );
  const showExperimentsFlag = useFeatureFlagEnabled(
    config?.postHog?.featureFlags?.showExperiments
  );

  const showHeaderFooter =
    !window.location.pathname.includes("ja-action") &&
    !window.location.pathname.includes("bh-action");

  const isPublicRoute = (path: string) => {
    const publicRoutes = ['/invitation', '/new-company', '/new-company-user', '/join'];
    return publicRoutes.some(route => path.startsWith(route));
  };

  const getProfile = async () => {
    try {
      const { data } = await client.query({ query: GET_CONNECTIONS });
      setConnections(data.connections);
    } catch (error) {
      // Silently handle connection errors
    }
    try {
      const { data } = await client.query({ query: GET_PROFILE });
      setUserProfile({ ...data.profile, communities: data.communities.items });
    } catch (error) {
      // Silently handle profile errors
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Skip auth check for public routes
        if (isPublicRoute(window.location.pathname)) {
          setAuthChecked(true);
          return;
        }

        // Check if returning from auth with tokens in URL hash
        const hashTokens = parseTokensFromHash();
        if (hashTokens) {
          storeTokens(hashTokens);
          clearHashFromUrl();

          // Identify user in PostHog
          posthog.identify(hashTokens.userId, {
            email: hashTokens.email,
          });

          setAuthChecked(true);

          // Load user profile
          await getProfile();
          return;
        }

        // Check for stored tokens (from previous auth)
        const storedTokens = getStoredTokens();
        if (storedTokens) {
          // Identify user in PostHog
          posthog.identify(storedTokens.userId, {
            email: storedTokens.email,
          });

          setAuthChecked(true);

          // Load user profile
          await getProfile();
          return;
        }

        // No valid tokens - redirect to auth
        redirectToAuth();
      } catch (error) {
        console.error('Authentication check failed:', error);
        // On error, redirect to auth
        redirectToAuth();
      }
    };

    checkAuth();
  }, []);

  // Welcome modal logic
  useEffect(() => {
    if (authChecked && !isPublicRoute(window.location.pathname)) {
      const hideWelcome =
        localStorage.getItem("hideWelcome") === "true" ||
        window.location.pathname === "/privacy";
      setShowWelcomeModal(!hideWelcome);
    }
  }, [authChecked]);

  if (isMaintenanceFlag) {
    return (
      <DownMaintenance>
        <h3>
          Role Relay is currently offline for system maintenance. Please check
          back soon.
        </h3>
      </DownMaintenance>
    );
  }

  if (!authChecked) {
    return (
      <LoadingContainer>
        <h3>Loading...</h3>
      </LoadingContainer>
    );
  }

  const appRoutes = (
    <Routes>
      <Route
        path="/"
        element={isDashboardFlag ? <Dashboard /> : <Home />}
      />
      <Route
        path="/public-roles"
        element={<PublicRoles user={userProfile} />}
      />
      <Route
        path="/public-roles/:roleId"
        element={<PublicRoles user={userProfile} />}
      />
      <Route
        path="/partner-roles"
        element={
          <PublicRoles
            user={userProfile}
            initialActiveTab="partner"
          />
        }
      />
      <Route
        path="/partner-roles/:roleId"
        element={
          <PublicRoles
            user={userProfile}
            initialActiveTab="partner"
          />
        }
      />
      <Route
        path="/community-roles"
        element={
          <PublicRoles
            user={userProfile}
            initialActiveTab="community"
          />
        }
      />
      <Route
        path="/community-roles/:roleId"
        element={
          <PublicRoles
            user={userProfile}
            initialActiveTab="community"
          />
        }
      />
      <Route
        path="/project-roles"
        element={
          <PublicRoles
            user={userProfile}
            initialActiveTab="project"
          />
        }
      />
      <Route
        path="/project-roles/:roleId"
        element={
          <PublicRoles
            user={userProfile}
            initialActiveTab="project"
          />
        }
      />
      <Route
        path="/public-candidates"
        element={<PublicCandidates user={userProfile} />}
      />
      <Route
        path="/public-candidates/:candidateId"
        element={<PublicCandidates user={userProfile} />}
      />
      <Route
        path="/partner-candidates"
        element={
          <PublicCandidates
            user={userProfile}
            initialActiveTab="partner"
          />
        }
      />
      <Route
        path="/partner-candidates/:candidateId"
        element={
          <PublicCandidates
            user={userProfile}
            initialActiveTab="partner"
          />
        }
      />
      <Route
        path="/community-candidates"
        element={
          <PublicCandidates
            user={userProfile}
            initialActiveTab="community"
          />
        }
      />
      <Route
        path="/community-candidates/:candidateId"
        element={
          <PublicCandidates
            user={userProfile}
            initialActiveTab="community"
          />
        }
      />
      <Route
        path="/project-candidates"
        element={
          <PublicCandidates
            user={userProfile}
            initialActiveTab="projects"
          />
        }
      />
      <Route
        path="/project-candidates/:candidateId"
        element={
          <PublicCandidates
            user={userProfile}
            initialActiveTab="projects"
          />
        }
      />
      <Route
        path="/my-roles"
        element={<Roles user={userProfile} />}
      />
      <Route
        path="/my-roles/:roleId"
        element={<Roles user={userProfile} />}
      />
      <Route
        path="/my-roles/:roleId/:matchId"
        element={<Roles user={userProfile} />}
      />
      <Route
        path="/my-candidates"
        element={<Candidates user={userProfile} />}
      />
      <Route
        path="/my-candidates/:candidateId"
        element={<Candidates user={userProfile} />}
      />
      <Route
        path="/my-candidates/:candidateId/:matchId"
        element={<Candidates user={userProfile} />}
      />
      <Route
        path="/matches"
        element={
          showMatchesFlag && <PublicMatches user={userProfile} />
        }
      />
      <Route
        path="/matches/:matchId"
        element={
          showMatchesFlag && <PublicMatches user={userProfile} />
        }
      />
      <Route
        path="/experiments"
        element={showExperimentsFlag && <Experiments />}
      />
      <Route
        path="/ja-action/:action/:id"
        element={<AddToProject user={userProfile} />}
      />
      <Route
        path="/bh-action/:action/:id"
        element={<BullhornAction />}
      />
      <Route path="/settings" element={<Settings />} />
      <Route
        path="/settings/:pathUrl"
        element={<Settings />}
      />
      <Route
        path="/messages"
        element={<Messages user={userProfile} activeTab="roles" />}
      />
      <Route
        path="/messages/roles"
        element={<Messages user={userProfile} activeTab="roles" />}
      />
      <Route
        path="/messages/candidates"
        element={
          <Messages user={userProfile} activeTab="candidates" />
        }
      />
      <Route
        path="/messages/:topicType/:topicId"
        element={<Messages user={userProfile} />}
      />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/dispute" element={<Dispute />} />
      <Route
        path="/connections/jobadder/callback"
        element={<JobAdderCallback />}
      />
      <Route
        path="/connections/vincere/callback"
        element={<VincereCallback />}
      />
      <Route
        path="/connections/bullhorn/callback"
        element={<BullhornCallback />}
      />
    </Routes>
  );

  return (
    <ThemeProvider theme={relayTheme}>
      <ProfileContext.Provider
        value={{ connections: connections, userProfile: userProfile }}
      >
        <BrowserRouter>
          <Routes>
            <Route
              path="/invitation"
              element={
                <ApolloProvider client={client}>
                  <UserInvitation />
                </ApolloProvider>
              }
            />
            <Route path="/new-company" element={<NewCompany />} />
            <Route
              path="/new-company-user"
              element={<NewCompanyUser user={userProfile} />}
            />
            <Route path="/join" element={<Join user={userProfile} />} />
            <Route
              path="*"
              element={
                <Main>
                  <ApolloProvider client={client}>
                    {/* Welcome Modal */}
                    <Dialog
                      open={showWelcomeModal}
                      onOpenChange={() => {
                        localStorage.setItem("hideWelcome", "true");
                        setShowWelcomeModal(false);
                      }}
                    >
                      <DialogContent className="sm:max-w-[600px]">
                        <DialogHeader>
                          <DialogTitle>Welcome to Role Relay!</DialogTitle>
                        </DialogHeader>
                        <div>
                          <p>
                            By proceeding you accept our{" "}
                            <a href={terms} target="_blank" rel="noreferrer">
                              terms and conditions
                            </a>{" "}
                            and{" "}
                            <a href="/privacy" target="_blank">
                              privacy policy
                            </a>
                          </p>
                        </div>
                        <DialogFooter>
                          <Button
                            onClick={() => {
                              localStorage.setItem("hideWelcome", "true");
                              setShowWelcomeModal(false);
                            }}
                          >
                            Proceed
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>

                    {/* Mobile Menu Button */}
                    {showHeaderFooter && (
                      <MobileMenuButton onClick={() => setSidebarOpen(true)}>
                        <Menu size={24} />
                      </MobileMenuButton>
                    )}

                    {/* Sidebar */}
                    {showHeaderFooter && (
                      <AppSidebar
                        open={sidebarOpen}
                        onClose={() => setSidebarOpen(false)}
                        showMatches={showMatchesFlag}
                        showExperiments={showExperimentsFlag}
                        user={userProfile}
                        onCollapseChange={setSidebarCollapsed}
                      />
                    )}

                    {/* Main Content Area */}
                    <AppShell $withSidebar={showHeaderFooter} $sidebarCollapsed={sidebarCollapsed}>
                      <ContentShell>
                        {appRoutes}
                        {/* {showHeaderFooter && <Footer />} */}
                      </ContentShell>
                    </AppShell>
                  </ApolloProvider>
                </Main>
              }
            />
          </Routes>
        </BrowserRouter>
      </ProfileContext.Provider>
    </ThemeProvider>
  );
};

export default App;
